import { type ItemAvailability } from 'schema-dts';
import {
	type ProductVariantFieldsFragment,
	type ProductStatusEnum,
	type Maybe,
	type ProductAttributeGroupFieldsFragment
} from '../../__generated__/graphql-client-types';
import { APPENDED_SITE_NAME } from '../../constants/general';

import {
	type PdpProductFamily,
	type PlaAttribute,
	type PlaAttributeGroup,
	type PlaProductFamily,
	type ProductNotFound
} from '../../types/product.types';
import { isAlternateProductSameAsProduct } from './restrictions.helper';

export function isProductNotFound(product: PlaProductFamily | PdpProductFamily | ProductNotFound): product is ProductNotFound {
	return product?.__typename === 'NotFoundError';
}

export const findVariantById = (
	variants: ProductVariantFieldsFragment[],
	id: number | undefined
): ProductVariantFieldsFragment | undefined => {
	if (!id) {
		return;
	}
	return variants.find((current) => current.id === id);
};

export const findVariant = (
	product?: PlaProductFamily | PdpProductFamily,
	id?: number,
	productStatus: ProductStatusEnum[] = ['stock']
): ProductVariantFieldsFragment | undefined => {
	if (id && product && !isProductNotFound(product)) {
		const filteredVariantsByStatus = filterVariantsByStatuses(product.variants, productStatus);
		const requestedVariant = findVariantById(product.variants, id);

		return findVariantById(filteredVariantsByStatus, requestedVariant?.id) ? requestedVariant : filteredVariantsByStatus[0];
	}
	if (product && product?.variants?.length) {
		// get first stock product
		const stockVariant = product.variants.find((variant) => variant.status === 'stock');

		return stockVariant ?? product.variants[0];
	}
	return undefined;
};

export const filterVariantsByStatuses = (
	variants: ProductVariantFieldsFragment[],
	statuses: ProductStatusEnum[]
): ProductVariantFieldsFragment[] => {
	return variants.filter((variant) => statuses.includes(variant.status));
};

export const canAddToCart = (productVariant: Maybe<ProductVariantFieldsFragment>) => {
	return productVariant && productVariant.priceInfo && !productVariant.priceInfo.packaging && !productVariant.saleRestrictions;
};

export const hasRestrictions = (variant: ProductVariantFieldsFragment, familyId): boolean => {
	const showRestrictionWarnings = isAlternateProductSameAsProduct(familyId, variant.saleRestrictions?.alternatives);
	if (showRestrictionWarnings) {
		return Boolean(variant.saleRestrictions);
	}
	return false;
};

export function createProductName(brandName: string, modelNumber: string): string {
	return `${brandName} ${modelNumber}`;
}

export function createProductPageTitle(brandName: string, modelNumber: string, productTitle: string): string {
	// We always want the brandName and model number to be intact and unaffected by truncation
	const productName = `${brandName} ${modelNumber} `;
	return `${productName}${productTitle}${APPENDED_SITE_NAME}`;
}

export function getKeySpecAttributes(
	attributeGroups: ProductAttributeGroupFieldsFragment[]
): ProductAttributeGroupFieldsFragment['attributes'] {
	return attributeGroups.flatMap((group) => group.attributes.filter((attr) => attr.featured && !attr.isHidden));
}

export function getFlattenedAttributes(attributeGroups: PlaAttributeGroup[]): Array<PlaAttribute> {
	return attributeGroups.flatMap((attributeGroup) => attributeGroup.attributes);
}

export function isShowroomOnlyProduct(selectedVariant: Pick<ProductVariantFieldsFragment, 'productPricingDisplay'>): boolean {
	return selectedVariant.productPricingDisplay === 'SHOW_ROOM_ONLY';
}

export function hasNonStockFinishes(product: PdpProductFamily): boolean {
	return Boolean(product.variants.length && product.variants.some((variant) => variant.status === 'nonstock'));
}

/**
 * Returns true if variantName is not equals to N/A, n/a, NA or na, false otherwise.
 *
 * @param {string} variantName
 * @returns boolean
 */
export const isApplicable = (variantName: string): boolean => {
	return variantName.replace('/', '').toLowerCase() !== 'na';
};

export function quotePhrase(term: string) {
	return term.includes(' ') ? `"${term}"` : term;
}

export const getExtraPaddingForUnderlinePosition = (isLarge: boolean) => {
	// we need to test for large because large & medium queries overlap at exactly 1024px (64em)
	return isLarge ? 24 : 7;
};

// copied from schema-dts@0.6.0 it seems to have been removed from future versions
const ItemAvailabilityMap = {
	Discontinued: 'http://schema.org/Discontinued',
	InStock: 'http://schema.org/InStock',
	InStoreOnly: 'http://schema.org/InStoreOnly',
	LimitedAvailability: 'http://schema.org/LimitedAvailability',
	OnlineOnly: 'http://schema.org/OnlineOnly',
	OutOfStock: 'http://schema.org/OutOfStock',
	PreOrder: 'http://schema.org/PreOrder',
	PreSale: 'http://schema.org/PreSale',
	SoldOut: 'http://schema.org/SoldOut'
};

export const convertRegionalStatusToAvailabilityEnum = (status = 'InStock'): ItemAvailability => {
	return ItemAvailabilityMap[status];
};

export const getSwatchStyle = (swatchGradient?: string, color: string = 'ffffff', swatchHeight = 10) => {
	return {
		...(swatchGradient ? { background: swatchGradient } : {}),
		backgroundColor: `#${color}`,
		height: swatchHeight
	};
};
